import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import ImageUploading, { ImageListType } from "react-images-uploading";
import { getAuth, signInWithCustomToken, signInWithEmailAndPassword } from "firebase/auth";
import { useDispatch, useSelector } from "react-redux";

import { LogoLettersBlack } from "../../../assets/images";
import { setIdToken } from "../../../slices/authSlice";
import {
  invitePartner,
  onboardPartner,
  checkPartnerEmail,
  getPlacesAutocomplete,
  getMbActivationCode,
  uploadLogoImage,
  uploadImage,
  updateGallery,
} from "../../../api/apiEndpoints";
import { allAmenitiesArr } from "../../../utils/arrays";
import {
  Page, LogoImg, Card, StepLabel, StepTitle, StepSubtitle,
  ProgressBar, ProgressDot, FieldGroup, FieldRow, Field, Label,
  Input, Textarea, Select, HelperText, ErrorText,
  AmenitiesGrid, AmenityChip, ImageUploadGrid, ImageSlot,
  ImageSlotPlus, ImageRemoveBtn, LogoUploadArea, NavRow,
  PrimaryButton, SecondaryButton, ReviewSection, ReviewTitle,
  ReviewRow, SuccessCard, SuccessIcon, SuccessTitle, SuccessText,
  Divider, PasswordToggleWrapper, PasswordToggleBtn,
  InfoCallout, InfoCalloutIcon, InfoCalloutText,
  CredentialBox, CredentialRow,
  AutocompleteWrapper, SuggestionList, SuggestionItem,
} from "./styles";

const TOTAL_STEPS = 6;

type IntegrationProvider =
  | "mindbody" | "marianatek" | "arketa" | "hapana"
  | "glofox" | "pushpress" | "clubready" | "recess" | "momence" | "";

const INTEGRATION_OPTIONS: { value: IntegrationProvider; label: string }[] = [
  { value: "",           label: "None / Not sure" },
  { value: "mindbody",   label: "MindBody" },
  { value: "marianatek", label: "Mariana Tek" },
  { value: "arketa",     label: "Arketa" },
  { value: "hapana",     label: "Hapana" },
  { value: "glofox",     label: "Glofox" },
  { value: "pushpress",  label: "PushPress" },
  { value: "clubready",  label: "Club Ready" },
  { value: "recess",     label: "Recess" },
  { value: "momence",    label: "Momence" },
];

interface CoOwnerEntry {
  email: string;
  password: string;
  exists: boolean;
  emailChecked: boolean;
  checking: boolean;
}

const BLANK_CO_OWNER: CoOwnerEntry = { email: "", password: "", exists: false, emailChecked: false, checking: false };

interface WizardForm {
  // Step 1 — Account
  email: string;
  password: string;
  confirmPassword: string;
  // Step 2 — Gym info
  gymName: string;
  description: string;
  address: string;
  amenities: string[];
  trainingStyles: string[];
  instagram: string;
  facebook: string;
  // Step 3 — Media (handled via refs/state separately)
  // Step 4 — Pricing
  trialPrice: string;
  monthlyPrice: string;
  quarterlyPrice: string;
  yearlyPrice: string;
  taxRate: string;
  // Step 5 — Integration
  integrationProvider: IntegrationProvider;
  siteId: string;
  // Step 2 extra
  hasClassBookings: boolean;
}

const BLANK: WizardForm = {
  email: "",
  password: "",
  confirmPassword: "",
  gymName: "",
  description: "",
  address: "",
  amenities: [],
  trainingStyles: [],
  instagram: "",
  facebook: "",
  trialPrice: "",
  monthlyPrice: "",
  quarterlyPrice: "",
  yearlyPrice: "",
  taxRate: "",
  integrationProvider: "",
  siteId: "",
  hasClassBookings: false,
};

const GymOnboardingWizard = () => {
  const { tokenValue } = useParams<{ tokenValue: string }>();
  const dispatch = useDispatch();
  const adminLoggedIn = useSelector((state: RootState) => state.auth?.adminLoggedIn);

  // Accepting an invite onto an existing account requires signing in as it,
  // so the backend can check the invite isn't being used to take it over.
  const signInExisting = async () => {
    try {
      const credential = await signInWithEmailAndPassword(getAuth(), form.email.trim(), form.password);
      dispatch(setIdToken(await credential.user.getIdToken()));
      return true;
    } catch {
      setError("That password doesn't match this account.");
      return false;
    }
  };

  const apiError = (e: any) =>
    e?.response?.data?.error || e?.response?.data?.message || e?.response?.data?.publicMessage || "Something went wrong.";
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<WizardForm>(BLANK);
  const [showPassword, setShowPassword] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [galleryImages, setGalleryImages] = useState<ImageListType>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [mbActivationCode, setMbActivationCode] = useState<string | null>(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [isCoOwnerInvite, setIsCoOwnerInvite] = useState(false);
  const [inviteChecking, setInviteChecking] = useState(true);
  const [existingAccount, setExistingAccount] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [coOwners, setCoOwners] = useState<CoOwnerEntry[]>([]);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!tokenValue) { setInviteChecking(false); return; }
    invitePartner(tokenValue)
      .then((res: any) => {
        if (res?.data?.businessId) {
          setIsCoOwnerInvite(true);
          if (res.data.email) {
            setForm((f) => ({ ...f, email: res.data.email }));
            return checkPartnerEmail(res.data.email)
              .then(({ data }: any) => setExistingAccount(!!data?.exists))
              .catch(() => setExistingAccount(false));
          }
        }
      })
      .catch(() => setError("Invalid or expired invite link."))
      .finally(() => setInviteChecking(false));
  }, [tokenValue]);

  const set = (key: keyof WizardForm, val: string) =>
    setForm((f) => ({ ...f, [key]: val }));

  const toggleAmenity = (name: string) =>
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(name)
        ? f.amenities.filter((a) => a !== name)
        : [...f.amenities, name],
    }));

  const toggleTrainingStyle = (name: string) =>
    setForm((f) => ({
      ...f,
      trainingStyles: f.trainingStyles.includes(name)
        ? f.trainingStyles.filter((s) => s !== name)
        : [...f.trainingStyles, name],
    }));

  const updateCoOwner = (index: number, patch: Partial<CoOwnerEntry>) =>
    setCoOwners((prev) => prev.map((co, i) => (i === index ? { ...co, ...patch } : co)));

  const addCoOwner = () => setCoOwners((prev) => [...prev, { ...BLANK_CO_OWNER }]);
  const removeCoOwner = (index: number) => setCoOwners((prev) => prev.filter((_, i) => i !== index));

  const handleCoOwnerEmailBlur = async (index: number) => {
    const co = coOwners[index];
    if (!co || !co.email || !/\S+@\S+\.\S+/.test(co.email)) return;
    updateCoOwner(index, { checking: true, emailChecked: false });
    try {
      const { data } = await checkPartnerEmail(co.email);
      updateCoOwner(index, { exists: data.exists, emailChecked: true, ...(data.exists ? { password: "" } : {}) });
    } catch {
      updateCoOwner(index, { exists: false, emailChecked: true });
    } finally {
      updateCoOwner(index, { checking: false });
    }
  };

  const handleEmailBlur = async () => {
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) return;
    setCheckingEmail(true);
    setEmailChecked(false);
    try {
      const { data } = await checkPartnerEmail(form.email);
      setExistingAccount(data.exists);
      setEmailChecked(true);
      if (data.exists) {
        setForm((f) => ({ ...f, password: "", confirmPassword: "" }));
      }
    } catch {
      setExistingAccount(false);
      setEmailChecked(true);
    } finally {
      setCheckingEmail(false);
    }
  };

  const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg"];

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError("Logo must be a PNG or JPG file.");
      e.target.value = "";
      return;
    }
    setError(null);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const quarterlyDefault = form.monthlyPrice
    ? String((parseFloat(form.monthlyPrice) * 3 * 0.9).toFixed(2))
    : "";
  const yearlyDefault = form.monthlyPrice
    ? String((parseFloat(form.monthlyPrice) * 12 * 0.85).toFixed(2))
    : "";

  const coOwnerValid = coOwners.every(
    (co) => !co.email || (co.emailChecked && !co.checking && (co.exists || co.password.length >= 6))
  );
  const canStep1 = emailChecked && !checkingEmail && coOwnerValid && (existingAccount
    ? !!form.email && (adminLoggedIn || form.password.length > 0)
    : form.email && form.password.length >= 6 && form.password === form.confirmPassword);
  const canStep2 = form.gymName.trim() && form.description.trim() && form.address.trim();
  const canStep4 = form.trialPrice && form.monthlyPrice && parseFloat(form.monthlyPrice) > 0;

  const handleGenerateCode = async () => {
    if (!form.siteId.trim()) return;
    setGeneratingCode(true);
    setMbActivationCode(null);
    try {
      const { data } = await getMbActivationCode({ inviteToken: tokenValue!, siteId: form.siteId.trim() });
      setMbActivationCode(data.activationCode);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.response?.data?.message ?? "Could not retrieve activation code. Check the Site ID and try again.");
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleStep1Next = async () => {
    setError(null);
    if (existingAccount && !adminLoggedIn) {
      setSubmitting(true);
      const ok = await signInExisting();
      setSubmitting(false);
      if (!ok) return;
    }
    handleNext();
  };

  const handleNext = () => {
    setError(null);
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setError(null);
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCoOwnerSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (existingAccount && !(await signInExisting())) return;
      const { data } = await onboardPartner({
        inviteToken: tokenValue!,
        ownerEmail: form.email,
        ...(existingAccount ? {} : { ownerPassword: form.password }),
        // Co-owner invites skip gym creation — backend detects businessId on invite
        name: "", description: "", address: { fullAddress: "" },
        amenities: [], trainingStyles: [], socialLinks: [],
        basicProductPrice: 75 as 75 | 119 | 199,
        premiumProductPrice: 0,
        hasClassBookings: false,
      });
      const auth = getAuth();
      const credential = await signInWithCustomToken(auth, data.customToken);
      const idToken = await credential.user.getIdToken();
      dispatch(setIdToken(idToken));
      setDone(true);
    } catch (e: any) {
      setError(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const quarterly = form.quarterlyPrice || quarterlyDefault;
      const yearly = form.yearlyPrice || yearlyDefault;

      const socialLinks = [
        form.instagram ? { name: "instagram", socialLink: form.instagram } : null,
        form.facebook  ? { name: "facebook",  socialLink: form.facebook  } : null,
      ].filter(Boolean) as { name: string; socialLink: string }[];

      // Single atomic call — creates Firebase account + DB user + business + Stripe products
      const { data } = await onboardPartner({
        inviteToken: tokenValue!,
        ownerEmail: form.email,
        ...(!existingAccount && form.password ? { ownerPassword: form.password } : {}),
        coOwners: coOwners.filter((co) => co.email).map((co) => ({
          email: co.email,
          ...(co.password ? { password: co.password } : {}),
        })),
        name: form.gymName.trim(),
        description: form.description.trim(),
        address: { fullAddress: form.address.trim() },
        amenities: form.amenities,
        trainingStyles: form.trainingStyles,
        socialLinks,
        basicProductPrice: parseInt(form.trialPrice, 10) as 75 | 119 | 199,
        premiumProductPrice: parseFloat(form.monthlyPrice),
        premiumQuarterlyProductPrice: quarterly ? parseFloat(quarterly) : undefined,
        premiumYearlyProductPrice: yearly ? parseFloat(yearly) : undefined,
        taxRateBps: form.taxRate ? parseFloat((parseFloat(form.taxRate) * 100).toFixed(2)) : undefined,
        hasClassBookings: form.hasClassBookings,
        // Integration — backend fetches MB activation code on-demand; no DB row yet
        integrationProvider: form.integrationProvider || undefined,
        siteId: form.integrationProvider === "mindbody" ? (form.siteId || undefined) : undefined,
      });

      // Sign in with the custom token, then push the Firebase ID token into Redux
      // so the api axios interceptor includes the Authorization header on file uploads
      const auth = getAuth();
      const credential = await signInWithCustomToken(auth, data.customToken);
      const idToken = await credential.user.getIdToken();
      dispatch(setIdToken(idToken));

      // Upload logo
      if (logoFile) {
        await uploadLogoImage(logoFile).catch(() => null);
      }

      // Upload gallery photos
      if (galleryImages.length > 0) {
        const uploaded: { resourceUri: string; contentType: string }[] = [];
        for (const img of galleryImages) {
          if (!img.file) continue;
          const fd = new FormData();
          fd.append("image", img.file);
          const res = await uploadImage(fd);
          uploaded.push({ resourceUri: res.data.resourceUri, contentType: res.data.contentType });
        }
        if (uploaded.length > 0) {
          await updateGallery(uploaded).catch(() => null);
        }
      }

      setDone(true);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.response?.data?.message ?? "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Co-owner simplified flow ───────────────────────────────────────────────
  if (inviteChecking) return null;

  if (isCoOwnerInvite && !done) {
    return (
      <Page>
        <LogoImg src={LogoLettersBlack} alt="JackedRabbit" />
        <Card>
          <StepTitle>{existingAccount ? "Accept your invite" : "Create your account"}</StepTitle>
          <StepSubtitle>
            {existingAccount
              ? "You've been invited to a studio's JackedRabbit partner dashboard. Sign in with your existing password to accept."
              : "You've been invited to a studio's JackedRabbit partner dashboard. Set your password to get started."}
          </StepSubtitle>
          <FieldGroup style={{ marginTop: 24 }}>
            <Field>
              <Label>Email</Label>
              <Input type="email" value={form.email} readOnly disabled />
            </Field>
            <Field>
              <Label>Password</Label>
              <Input
                type="password"
                placeholder={existingAccount ? "Your current password" : "Min. 6 characters"}
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                autoComplete={existingAccount ? "current-password" : "new-password"}
              />
            </Field>
            {!existingAccount && (
              <Field>
                <Label>Confirm password</Label>
                <Input type="password" placeholder="Repeat password" value={form.confirmPassword} onChange={(e) => set("confirmPassword", e.target.value)} autoComplete="new-password" />
              </Field>
            )}
          </FieldGroup>
          {error && <ErrorText>{error}</ErrorText>}
          <PrimaryButton
            style={{ marginTop: 24 }}
            disabled={
              !form.email ||
              submitting ||
              (existingAccount
                ? form.password.length === 0
                : form.password.length < 6 || form.password !== form.confirmPassword)
            }
            onClick={handleCoOwnerSubmit}
          >
            {submitting
              ? existingAccount ? "Signing in…" : "Creating account…"
              : existingAccount ? "Sign in and accept" : "Create account"}
          </PrimaryButton>
        </Card>
      </Page>
    );
  }

  if (done) {
    return (
      <Page>
        <LogoImg src={LogoLettersBlack} alt="JackedRabbit" />
        <SuccessCard>
          <SuccessIcon>🎉</SuccessIcon>
          <SuccessTitle>{isCoOwnerInvite ? "You're in!" : "You're all set!"}</SuccessTitle>
          <SuccessText>
            {isCoOwnerInvite
              ? existingAccount
                ? "The studio has been added to your account. Click below to open your dashboard."
                : "Your account has been created. Click below to log in and view your gym dashboard."
              : <><strong>{form.gymName}</strong> has been successfully onboarded. The JackedRabbit team will review the profile and make it live once approved.</>
            }
          </SuccessText>
          {isCoOwnerInvite && (
            <PrimaryButton
              style={{ marginTop: 24, width: '100%' }}
              onClick={() => window.location.href = '/'}
            >
              Log in to your dashboard
            </PrimaryButton>
          )}
          {mbActivationCode && (
            <SuccessText style={{ marginTop: 16 }}>
              <strong>MindBody activation code:</strong>{" "}
              <span style={{ fontFamily: "monospace", letterSpacing: 1 }}>{mbActivationCode}</span>
              <br />
              Share this with the gym owner and email matt@ &amp; kenneth@jackedrabbitgyms.com once they've entered it.
            </SuccessText>
          )}
          {form.integrationProvider === "marianatek" && (
            <SuccessText style={{ marginTop: 16 }}>
              Don't forget to send the Mariana Tek setup email to{" "}
              <strong>support@marianatek.com</strong> with the gym owner CC'd.
            </SuccessText>
          )}
          {form.integrationProvider && !["mindbody", "marianatek", ""].includes(form.integrationProvider) && (
            <SuccessText style={{ marginTop: 16 }}>
              Follow up with Kenneth at{" "}
              <strong>kenneth@jackedrabbitgyms.com</strong> about the{" "}
              {INTEGRATION_OPTIONS.find(o => o.value === form.integrationProvider)?.label} integration.
            </SuccessText>
          )}
        </SuccessCard>
      </Page>
    );
  }

  return (
    <Page>
      <LogoImg src={LogoLettersBlack} alt="JackedRabbit" />
      <Card>
        <ProgressBar>
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <ProgressDot key={i} done={i + 1 < step} active={i + 1 === step} />
          ))}
        </ProgressBar>

        {step === 1 && <Step1 form={form} set={set} showPassword={showPassword}
          setShowPassword={setShowPassword} error={error} submitting={submitting}
          canProceed={!!canStep1} onNext={handleStep1Next}
          existingAccount={existingAccount} adminLoggedIn={adminLoggedIn} checkingEmail={checkingEmail}
          onEmailBlur={handleEmailBlur}
          emailChecked={emailChecked}
          onEmailChange={(val: string) => { set("email", val); setExistingAccount(false); setEmailChecked(false); }}
          coOwners={coOwners}
          onCoOwnerEmailChange={(index: number, val: string) => updateCoOwner(index, { email: val, exists: false, emailChecked: false, password: "" })}
          onCoOwnerEmailBlur={handleCoOwnerEmailBlur}
          onCoOwnerPasswordChange={(index: number, val: string) => updateCoOwner(index, { password: val })}
          onAddCoOwner={addCoOwner}
          onRemoveCoOwner={removeCoOwner}
        />}

        {step === 2 && <Step2 form={form} set={set} toggleAmenity={toggleAmenity} toggleTrainingStyle={toggleTrainingStyle}
          toggleClassBookings={(val: boolean) => setForm((f) => ({ ...f, hasClassBookings: val }))}
          error={error} canProceed={!!canStep2} onNext={handleNext} onBack={handleBack} />}

        {step === 3 && <Step3 logoPreview={logoPreview} logoInputRef={logoInputRef}
          onLogoSelect={handleLogoSelect} onLogoRemove={() => { setLogoFile(null); setLogoPreview(null); }}
          galleryImages={galleryImages} onGalleryChange={setGalleryImages}
          onError={setError}
          error={error} onNext={handleNext} onBack={handleBack} />}

        {step === 4 && <Step4 form={form} set={set} quarterlyDefault={quarterlyDefault}
          yearlyDefault={yearlyDefault} error={error} canProceed={!!canStep4}
          onNext={handleNext} onBack={handleBack} />}

        {step === 5 && <Step5 form={form} set={set} error={error}
          mbActivationCode={mbActivationCode}
          generatingCode={generatingCode}
          onGenerateCode={handleGenerateCode}
          onProviderChange={(val: string) => { set("integrationProvider", val); set("siteId", ""); setMbActivationCode(null); }}
          onSiteIdChange={(val: string) => { set("siteId", val); setMbActivationCode(null); }}
          onNext={handleNext} onBack={handleBack} />}

        {step === 6 && <Step6 form={form} logoPreview={logoPreview}
          galleryCount={galleryImages.length} quarterlyDefault={quarterlyDefault}
          yearlyDefault={yearlyDefault} mbActivationCode={mbActivationCode}
          existingAccount={existingAccount}
          coOwners={coOwners}
          error={error} submitting={submitting}
          onSubmit={handleSubmit} onBack={handleBack} />}
      </Card>
    </Page>
  );
};

// ─── Step 1: Account ──────────────────────────────────────────────────────────

const Step1 = ({ form, set, showPassword, setShowPassword, error, submitting, canProceed, onNext,
  existingAccount, adminLoggedIn, emailChecked, checkingEmail, onEmailBlur, onEmailChange,
  coOwners, onCoOwnerEmailChange, onCoOwnerEmailBlur, onCoOwnerPasswordChange, onAddCoOwner, onRemoveCoOwner }: any) => (
  <>
    <StepLabel>Step 1 of {TOTAL_STEPS}</StepLabel>
    <StepTitle>Set up the gym owner's login</StepTitle>
    <StepSubtitle>Create the account the gym owner will use to access their JackedRabbit dashboard.</StepSubtitle>

    <InfoCallout>
      <InfoCalloutIcon>⚠️</InfoCalloutIcon>
      <InfoCalloutText>
        <strong>Use the gym owner's email — not yours.</strong> These are the credentials
        they will log in with. Write them down or copy them before continuing — you will
        need to share them with the gym owner after onboarding is complete.
      </InfoCalloutText>
    </InfoCallout>

    <FieldGroup>
      <Field>
        <Label>Gym owner's email *</Label>
        <Input
          type="email"
          value={form.email}
          onChange={(e: any) => onEmailChange(e.target.value)}
          onBlur={onEmailBlur}
          placeholder="owner@theirgym.com"
          autoComplete="off"
        />
        {checkingEmail && <HelperText>Checking account…</HelperText>}
      </Field>

      {emailChecked && existingAccount && adminLoggedIn && (
        <InfoCallout>
          <InfoCalloutIcon>✓</InfoCalloutIcon>
          <InfoCalloutText>
            <strong>Account found.</strong> This owner already has a JackedRabbit account.
            You're signed in as a JackedRabbit admin, so the new gym will be added to their
            existing account.
          </InfoCalloutText>
        </InfoCallout>
      )}

      {emailChecked && existingAccount && !adminLoggedIn && (
        <>
          <InfoCallout>
            <InfoCalloutIcon>✓</InfoCalloutIcon>
            <InfoCalloutText>
              <strong>Account found.</strong> This email already has a JackedRabbit account. Enter
              its password to confirm it's yours, and the new gym will be added to it.
            </InfoCalloutText>
          </InfoCallout>
          <Field>
            <Label>Account password *</Label>
            <PasswordToggleWrapper>
              <Input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e: any) => set("password", e.target.value)}
                placeholder="Current password"
                style={{ paddingRight: 56 }}
                autoComplete="current-password"
              />
              <PasswordToggleBtn type="button" onClick={() => setShowPassword((s: boolean) => !s)}>
                {showPassword ? "Hide" : "Show"}
              </PasswordToggleBtn>
            </PasswordToggleWrapper>
          </Field>
        </>
      )}

      {emailChecked && !existingAccount && (
        <>
          <Field>
            <Label>Password *</Label>

            <PasswordToggleWrapper>
              <Input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e: any) => set("password", e.target.value)}
                placeholder="Minimum 6 characters"
                style={{ paddingRight: 56 }}
                autoComplete="new-password"
              />
              <PasswordToggleBtn type="button" onClick={() => setShowPassword((s: boolean) => !s)}>
                {showPassword ? "Hide" : "Show"}
              </PasswordToggleBtn>
            </PasswordToggleWrapper>
            {form.password.length > 0 && form.password.length < 6 && (
              <ErrorText>Password must be at least 6 characters.</ErrorText>
            )}
          </Field>
          <Field>
            <Label>Confirm password *</Label>
            <Input
              type="password"
              value={form.confirmPassword}
              onChange={(e: any) => set("confirmPassword", e.target.value)}
              placeholder="Re-enter password"
              autoComplete="new-password"
            />
            {form.confirmPassword.length > 0 && form.password !== form.confirmPassword && (
              <ErrorText>Passwords do not match.</ErrorText>
            )}
          </Field>

          {form.email && form.password.length >= 6 && form.password === form.confirmPassword && (
            <>
              <Divider />
              <div>
                <Label style={{ marginBottom: 8, display: "block" }}>📋 Login credentials to share with gym owner</Label>
                <CredentialBox>
                  <CredentialRow>
                    <span>Email</span>
                    <span>{form.email}</span>
                  </CredentialRow>
                  <CredentialRow>
                    <span>Password</span>
                    <span>{form.password}</span>
                  </CredentialRow>
                </CredentialBox>
              </div>
            </>
          )}
        </>
      )}

      {emailChecked && (
        <>
          <Divider />
          <Label style={{ marginBottom: 4, display: "block" }}>Co-owners (optional)</Label>
          <HelperText style={{ marginBottom: 12 }}>
            If this gym has additional owners, add them here. Tab out of each email to check if they already have an account.
          </HelperText>

          {coOwners.map((co: CoOwnerEntry, index: number) => (
            <div key={index} style={{ border: "1px solid #e8e8e8", borderRadius: 10, padding: "16px 14px", marginBottom: 10, position: "relative" }}>
              <button
                type="button"
                onClick={() => onRemoveCoOwner(index)}
                style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#aaa", lineHeight: 1, padding: 0 }}
              >
                ×
              </button>
              <Field>
                <Label>Co-owner {index + 1} email</Label>
                <Input
                  type="email"
                  value={co.email}
                  onChange={(e: any) => onCoOwnerEmailChange(index, e.target.value)}
                  onBlur={() => onCoOwnerEmailBlur(index)}
                  placeholder="coowner@theirgym.com"
                  autoComplete="off"
                />
                {co.checking && <HelperText>Checking account…</HelperText>}
              </Field>

              {co.emailChecked && co.exists && (
                <InfoCallout style={{ marginTop: 10, marginBottom: 0 }}>
                  <InfoCalloutIcon>✓</InfoCalloutIcon>
                  <InfoCalloutText>
                    <strong>Account found.</strong> This co-owner already has a JackedRabbit account and will be added directly.
                  </InfoCalloutText>
                </InfoCallout>
              )}

              {co.emailChecked && !co.exists && co.email && (
                <Field style={{ marginTop: 10 }}>
                  <Label>Co-owner {index + 1} password *</Label>
                  <Input
                    type="password"
                    value={co.password}
                    onChange={(e: any) => onCoOwnerPasswordChange(index, e.target.value)}
                    placeholder="Minimum 6 characters"
                    autoComplete="new-password"
                  />
                  {co.password.length > 0 && co.password.length < 6 && (
                    <ErrorText>Password must be at least 6 characters.</ErrorText>
                  )}
                  <HelperText>Set a password for this co-owner's new account. Share it with them after onboarding.</HelperText>
                </Field>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={onAddCoOwner}
            style={{ background: "none", border: "1px dashed #ccc", borderRadius: 8, padding: "10px 16px", cursor: "pointer", fontSize: 13, color: "#555", fontFamily: "Aileron, sans-serif", width: "100%", textAlign: "center" }}
          >
            + Add co-owner
          </button>
        </>
      )}
    </FieldGroup>

    {error && <ErrorText style={{ marginTop: 8 }}>{error}</ErrorText>}
    <NavRow>
      <span />
      <PrimaryButton onClick={onNext} disabled={!canProceed || checkingEmail}>
        Continue →
      </PrimaryButton>
    </NavRow>
  </>
);

// ─── Address Autocomplete ─────────────────────────────────────────────────────

const AddressAutocomplete = ({ value, onChange }: { value: string; onChange: (val: string) => void }) => {
  const [suggestions, setSuggestions] = useState<{ description: string; placeId: string }[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (val: string) => {
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await getPlacesAutocomplete(val);
        setSuggestions(data.predictions);
        setOpen(data.predictions.length > 0);
      } catch {
        setSuggestions([]);
        setOpen(false);
      }
    }, 300);
  };

  const handleSelect = (description: string) => {
    onChange(description);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <AutocompleteWrapper ref={wrapperRef}>
      <Input
        value={value}
        onChange={(e: any) => handleChange(e.target.value)}
        placeholder="123 Main St, Miami, FL 33101"
        autoComplete="off"
      />
      {open && (
        <SuggestionList>
          {suggestions.map((s) => (
            <SuggestionItem key={s.placeId} onMouseDown={() => handleSelect(s.description)}>
              {s.description}
            </SuggestionItem>
          ))}
        </SuggestionList>
      )}
    </AutocompleteWrapper>
  );
};

// ─── Step 2: Gym Info ─────────────────────────────────────────────────────────

const TRAINING_STYLE_OPTIONS = [
  "Gym", "Yoga", "Cycling", "Boxing", "HIIT", "Hyrox",
  "Mat Pilates", "Reformer Pilates", "Crossfit", "Heated classes", "Barre", "Lagree",
];

const Step2 = ({ form, set, toggleAmenity, toggleTrainingStyle, toggleClassBookings, error, canProceed, onNext, onBack }: any) => (
  <>
    <StepLabel>Step 2 of {TOTAL_STEPS}</StepLabel>
    <StepTitle>Gym details</StepTitle>
    <StepSubtitle>Tell us about the gym. This is what members will see on the app.</StepSubtitle>
    <FieldGroup>
      <Field>
        <Label>Gym name *</Label>
        <Input value={form.gymName} onChange={(e: any) => set("gymName", e.target.value)} placeholder="e.g. Iron Paradise" />
      </Field>
      <Field>
        <Label>Description *</Label>
        <Textarea value={form.description} onChange={(e: any) => set("description", e.target.value)} placeholder="Describe the gym — equipment, vibe, specialties…" rows={3} />
      </Field>
      <Field>
        <Label>Full address *</Label>
        <AddressAutocomplete value={form.address} onChange={(val) => set("address", val)} />
        <HelperText>Start typing to search for the address.</HelperText>
      </Field>
      <Field>
        <Label>Amenities</Label>
        <AmenitiesGrid>
          {allAmenitiesArr.map(({ amenityName }) => (
            <AmenityChip
              key={amenityName}
              type="button"
              selected={form.amenities.includes(amenityName)}
              onClick={() => toggleAmenity(amenityName)}
            >
              {amenityName}
            </AmenityChip>
          ))}
        </AmenitiesGrid>
      </Field>
      <Field>
        <Label>Workout types</Label>
        <AmenitiesGrid>
          {TRAINING_STYLE_OPTIONS.map((style) => (
            <AmenityChip
              key={style}
              type="button"
              selected={form.trainingStyles.includes(style)}
              onClick={() => toggleTrainingStyle(style)}
            >
              {style}
            </AmenityChip>
          ))}
        </AmenitiesGrid>
        <HelperText>Select all that apply — this powers how members find the studio.</HelperText>
      </Field>
      <Field>
        <Label>Does this location offer class bookings? *</Label>
        <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
          <AmenityChip
            type="button"
            selected={form.hasClassBookings === true}
            onClick={() => toggleClassBookings(true)}
            style={{ flex: 1, padding: "10px 8px" }}
          >
            Yes
          </AmenityChip>
          <AmenityChip
            type="button"
            selected={form.hasClassBookings === false}
            onClick={() => toggleClassBookings(false)}
            style={{ flex: 1, padding: "10px 8px" }}
          >
            No
          </AmenityChip>
        </div>
        <HelperText>Studios with bookable classes (Pilates, yoga, etc.) should select Yes.</HelperText>
      </Field>
      <Divider />
      <FieldRow>
        <Field>
          <Label>Instagram URL</Label>
          <Input value={form.instagram} onChange={(e: any) => set("instagram", e.target.value)} placeholder="https://instagram.com/…" />
        </Field>
        <Field>
          <Label>Facebook URL</Label>
          <Input value={form.facebook} onChange={(e: any) => set("facebook", e.target.value)} placeholder="https://facebook.com/…" />
        </Field>
      </FieldRow>
    </FieldGroup>
    {error && <ErrorText style={{ marginTop: 8 }}>{error}</ErrorText>}
    <NavRow>
      <SecondaryButton onClick={onBack}>← Back</SecondaryButton>
      <PrimaryButton onClick={onNext} disabled={!canProceed}>Continue →</PrimaryButton>
    </NavRow>
  </>
);

// ─── Step 3: Media ────────────────────────────────────────────────────────────

const Step3 = ({ logoPreview, logoInputRef, onLogoSelect, onLogoRemove, galleryImages, onGalleryChange, onError, error, onNext, onBack }: any) => {
  const dragIndex = useRef<number | null>(null);

  const handleDragStart = (i: number) => { dragIndex.current = i; };

  const handleDrop = (i: number) => {
    const from = dragIndex.current;
    if (from === null || from === i) return;
    const reordered = [...galleryImages];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(i, 0, moved);
    onGalleryChange(reordered);
    dragIndex.current = null;
  };

  return (
    <>
      <StepLabel>Step 3 of {TOTAL_STEPS}</StepLabel>
      <StepTitle>Photos & logo</StepTitle>
      <StepSubtitle>Upload a gym logo and up to 3 gallery photos. These appear on the app listing.</StepSubtitle>
      <FieldGroup>
        <Field>
          <Label>Gym logo</Label>
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg" style={{ display: "none" }} onChange={onLogoSelect} />
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <LogoUploadArea hasImage={!!logoPreview} onClick={() => logoInputRef.current?.click()}>
              {logoPreview
                ? <><img src={logoPreview} alt="Logo" /><ImageRemoveBtn onClick={(e: any) => { e.stopPropagation(); onLogoRemove(); }}>×</ImageRemoveBtn></>
                : <ImageSlotPlus>+</ImageSlotPlus>}
            </LogoUploadArea>
            <HelperText style={{ maxWidth: 200 }}>PNG or JPG only. Square format preferred. Shows next to the gym name on the app.</HelperText>
          </div>
        </Field>
        <Field>
          <Label>Gallery photos (up to 3)</Label>
          <ImageUploading
            multiple
            value={galleryImages}
            onChange={onGalleryChange}
            maxNumber={3}
            dataURLKey="dataURL"
            acceptType={["jpg", "jpeg", "png"]}
            onError={(errors: any) => {
              if (errors?.acceptType) onError("Gallery photos must be PNG or JPG files.");
            }}
          >
            {({ imageList, onImageUpload, onImageRemove }) => (
              <ImageUploadGrid>
                {imageList.map((img, i) => (
                  <ImageSlot
                    key={i}
                    hasImage
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e: any) => e.preventDefault()}
                    onDrop={() => handleDrop(i)}
                    style={{ cursor: "grab" }}
                  >
                    <img src={img.dataURL} alt={`Gallery ${i + 1}`} />
                    <ImageRemoveBtn onClick={() => onImageRemove(i)}>×</ImageRemoveBtn>
                  </ImageSlot>
                ))}
                {imageList.length < 3 && (
                  <ImageSlot hasImage={false} onClick={onImageUpload}>
                    <ImageSlotPlus>+</ImageSlotPlus>
                  </ImageSlot>
                )}
              </ImageUploadGrid>
            )}
          </ImageUploading>
          <HelperText>PNG or JPG only. Landscape photos work best (4:3 ratio). Drag to reorder.</HelperText>
        </Field>
      </FieldGroup>
      {error && <ErrorText style={{ marginTop: 8 }}>{error}</ErrorText>}
      <NavRow>
        <SecondaryButton onClick={onBack}>← Back</SecondaryButton>
        <PrimaryButton onClick={onNext}>Continue →</PrimaryButton>
      </NavRow>
    </>
  );
};

const TRIAL_PRICE_OPTIONS = [75, 119, 199] as const;

// ─── Step 4: Pricing ──────────────────────────────────────────────────────────

const Step4 = ({ form, set, quarterlyDefault, yearlyDefault, error, canProceed, onNext, onBack }: any) => (
  <>
    <StepLabel>Step 4 of {TOTAL_STEPS}</StepLabel>
    <StepTitle>Pricing</StepTitle>
    <StepSubtitle>Set the membership prices for this gym. We'll automatically create all 4 products in Stripe.</StepSubtitle>
    <FieldGroup>
      <Field>
        <Label>Monthly trial price *</Label>
        <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
          {TRIAL_PRICE_OPTIONS.map((price) => (
            <AmenityChip
              key={price}
              type="button"
              selected={form.trialPrice === String(price)}
              onClick={() => set("trialPrice", String(price))}
              style={{ flex: 1, padding: "10px 8px", fontSize: 13 }}
            >
              ${price}/mo
            </AmenityChip>
          ))}
        </div>
        <HelperText>Select the monthly trial price for this gym's location.</HelperText>
      </Field>
      <Field>
        <Label>Monthly upgrade price ($) *</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={form.monthlyPrice}
          onChange={(e: any) => set("monthlyPrice", e.target.value)}
          placeholder="e.g. 120"
        />
      </Field>
      <FieldRow>
        <Field>
          <Label>Quarterly price ($)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.quarterlyPrice || quarterlyDefault}
            onChange={(e: any) => set("quarterlyPrice", e.target.value)}
            placeholder={quarterlyDefault || "Auto (3× monthly)"}
          />
          <HelperText>Billed every 3 months. 10% discount applied automatically.</HelperText>
        </Field>
        <Field>
          <Label>Yearly price ($)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.yearlyPrice || yearlyDefault}
            onChange={(e: any) => set("yearlyPrice", e.target.value)}
            placeholder={yearlyDefault || "Auto (12× monthly)"}
          />
          <HelperText>Billed annually. 15% discount applied automatically.</HelperText>
        </Field>
      </FieldRow>
      <Field>
        <Label>Local sales tax rate (%)</Label>
        <Input
          type="number"
          min="0"
          max="30"
          step="0.001"
          value={form.taxRate}
          onChange={(e: any) => set("taxRate", e.target.value)}
          placeholder="e.g. 7.5"
        />
        <HelperText>Enter the sales tax percentage for the gym's location (e.g. 7 for 7%).</HelperText>
      </Field>
    </FieldGroup>
    {error && <ErrorText style={{ marginTop: 8 }}>{error}</ErrorText>}
    <NavRow>
      <SecondaryButton onClick={onBack}>← Back</SecondaryButton>
      <PrimaryButton onClick={onNext} disabled={!canProceed}>Continue →</PrimaryButton>
    </NavRow>
  </>
);

// ─── Step 5: Integration ──────────────────────────────────────────────────────

const Step5 = ({ form, set, error, mbActivationCode, generatingCode, onGenerateCode, onProviderChange, onSiteIdChange, onNext, onBack }: any) => {
  const isOther = form.integrationProvider &&
    form.integrationProvider !== "mindbody" &&
    form.integrationProvider !== "marianatek";

  const gymOwnerEmail = form.email || "gymowner@theirstudio.com";
  const gymName = form.gymName || "Gym Name";

  const mtEmailSubject = `Mariana Tek Integration Install — ${gymName}`;
  const mtEmailBody =
`Hi,

I'd like to request the Mariana Tek integration install for ${gymName}.

Studio Contact:
${gymOwnerEmail}

Please let me know if you need anything further from me. I'll look out for confirmation and the tenant-specific credentials once installed.

Thanks,`;

  return (
    <>
      <StepLabel>Step 5 of {TOTAL_STEPS}</StepLabel>
      <StepTitle>Integration</StepTitle>
      <StepSubtitle>
        Does this gym use a booking software? Select the platform below.
      </StepSubtitle>
      <FieldGroup>
        <Field>
          <Label>Booking platform</Label>
          <Select
            value={form.integrationProvider}
            onChange={(e: any) => onProviderChange(e.target.value)}
          >
            {INTEGRATION_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
        </Field>

        {/* MindBody — site ID + on-demand activation code */}
        {form.integrationProvider === "mindbody" && (
          <>
            <Field>
              <Label>MindBody Site ID</Label>
              <Input
                value={form.siteId}
                onChange={(e: any) => onSiteIdChange(e.target.value)}
                placeholder="e.g. 12345"
              />
              <HelperText>Found in the gym's MindBody account under Account → Locations.</HelperText>
            </Field>

            {form.siteId.trim() && !mbActivationCode && (
              <PrimaryButton
                type="button"
                onClick={onGenerateCode}
                disabled={generatingCode}
                style={{ alignSelf: "flex-start" }}
              >
                {generatingCode ? "Retrieving…" : "Generate activation code →"}
              </PrimaryButton>
            )}

            {mbActivationCode && (
              <InfoCallout>
                <InfoCalloutIcon>✓</InfoCalloutIcon>
                <InfoCalloutText>
                  <strong>Activation code retrieved:</strong>
                  <br />
                  <span style={{ fontFamily: "monospace", fontSize: 16, letterSpacing: 2 }}>
                    {mbActivationCode}
                  </span>
                  <br /><br />
                  Pass this code to the gym owner — they need to enter it in their MindBody
                  account under <em>Settings → Integrations</em> to authorize JackedRabbit's
                  access. Once they've done it, let{" "}
                  <strong>matt@jackedrabbitgyms.com</strong> and{" "}
                  <strong>kenneth@jackedrabbitgyms.com</strong> know via email.
                </InfoCalloutText>
              </InfoCallout>
            )}
          </>
        )}

        {/* Mariana Tek — email template */}
        {form.integrationProvider === "marianatek" && (
          <InfoCallout>
            <InfoCalloutIcon>✉️</InfoCalloutIcon>
            <InfoCalloutText>
              <strong>Send the following email after submitting:</strong>
              <br /><br />
              <strong>To:</strong> support@marianatek.com<br />
              <strong>CC:</strong> {gymOwnerEmail}, kenneth@jackedrabbitgyms.com<br />
              <strong>Subject:</strong> {mtEmailSubject}
              <br /><br />
              <span style={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: 12 }}>{mtEmailBody}</span>
            </InfoCalloutText>
          </InfoCallout>
        )}

        {/* All other integrations — follow-up email */}
        {isOther && (
          <InfoCallout>
            <InfoCalloutIcon>ℹ️</InfoCalloutIcon>
            <InfoCalloutText>
              Email <strong>kenneth@jackedrabbitgyms.com</strong> with the gym name and{" "}
              <strong>{INTEGRATION_OPTIONS.find(o => o.value === form.integrationProvider)?.label}</strong> as the
              integration partner. The team will follow up with setup instructions.
            </InfoCalloutText>
          </InfoCallout>
        )}
      </FieldGroup>
      {error && <ErrorText style={{ marginTop: 8 }}>{error}</ErrorText>}
      <NavRow>
        <SecondaryButton onClick={onBack}>← Back</SecondaryButton>
        <PrimaryButton onClick={onNext}>Continue →</PrimaryButton>
      </NavRow>
    </>
  );
};

// ─── Step 6: Review ───────────────────────────────────────────────────────────

const Step6 = ({ form, logoPreview, galleryCount, quarterlyDefault, yearlyDefault, mbActivationCode, existingAccount, coOwners, error, submitting, onSubmit, onBack }: any) => (
  <>
    <StepLabel>Step 6 of {TOTAL_STEPS}</StepLabel>
    <StepTitle>Review & submit</StepTitle>
    <StepSubtitle>Double-check everything before submitting. Your gym will be hidden until our team approves it.</StepSubtitle>

    <ReviewSection>
      <ReviewTitle>Gym Owner Login</ReviewTitle>
      {existingAccount ? (
        <InfoCallout>
          <InfoCalloutIcon>✓</InfoCalloutIcon>
          <InfoCalloutText>
            This gym will be added to the existing account for <strong>{form.email}</strong>.
            No password changes will be made.
          </InfoCalloutText>
        </InfoCallout>
      ) : (
        <>
          <InfoCallout>
            <InfoCalloutIcon>📋</InfoCalloutIcon>
            <InfoCalloutText>
              <strong>Save these credentials before submitting.</strong> Share them with the gym owner
              after onboarding — this is the last time they'll be shown in plaintext.
            </InfoCalloutText>
          </InfoCallout>
          <CredentialBox>
            <CredentialRow><span>Email</span><span>{form.email}</span></CredentialRow>
            <CredentialRow><span>Password</span><span>{form.password}</span></CredentialRow>
          </CredentialBox>
        </>
      )}
      {coOwners.filter((co: CoOwnerEntry) => co.email).map((co: CoOwnerEntry, i: number) => (
        <div key={i}>
          <ReviewRow style={{ marginTop: i === 0 ? 8 : 0 }}>
            <span>Co-owner {i + 1} email</span><span>{co.email}</span>
          </ReviewRow>
          {co.password ? (
            <ReviewRow>
              <span>Co-owner {i + 1} password</span><span>{co.password}</span>
            </ReviewRow>
          ) : (
            <ReviewRow>
              <span>Co-owner {i + 1}</span><span>Existing account — no password needed</span>
            </ReviewRow>
          )}
        </div>
      ))}
    </ReviewSection>

    <ReviewSection>
      <ReviewTitle>Gym Info</ReviewTitle>
      <ReviewRow><span>Name</span><span>{form.gymName}</span></ReviewRow>
      <ReviewRow><span>Address</span><span>{form.address}</span></ReviewRow>
      <ReviewRow><span>Class bookings</span><span>{form.hasClassBookings ? "Yes" : "No"}</span></ReviewRow>
      <ReviewRow><span>Amenities</span><span>{form.amenities.length > 0 ? form.amenities.join(", ") : "None selected"}</span></ReviewRow>
      <ReviewRow><span>Workout types</span><span>{form.trainingStyles.length > 0 ? form.trainingStyles.join(", ") : "None selected"}</span></ReviewRow>
    </ReviewSection>

    <ReviewSection>
      <ReviewTitle>Media</ReviewTitle>
      <ReviewRow><span>Logo</span><span>{logoPreview ? "✓ Uploaded" : "Not uploaded"}</span></ReviewRow>
      <ReviewRow><span>Gallery photos</span><span>{galleryCount} / 3</span></ReviewRow>
    </ReviewSection>

    <ReviewSection>
      <ReviewTitle>Pricing</ReviewTitle>
      <ReviewRow><span>Trial (basic)</span><span>${form.trialPrice} / month</span></ReviewRow>
      <ReviewRow><span>Monthly upgrade</span><span>${form.monthlyPrice} / month</span></ReviewRow>
      <ReviewRow><span>Quarterly upgrade</span><span>${form.quarterlyPrice || quarterlyDefault} / 3 months</span></ReviewRow>
      <ReviewRow><span>Yearly upgrade</span><span>${form.yearlyPrice || yearlyDefault} / year</span></ReviewRow>
      <ReviewRow><span>Sales tax rate</span><span>{form.taxRate ? `${form.taxRate}%` : "Not set"}</span></ReviewRow>
    </ReviewSection>

    <ReviewSection>
      <ReviewTitle>Integration</ReviewTitle>
      <ReviewRow>
        <span>Platform</span>
        <span>{INTEGRATION_OPTIONS.find(o => o.value === form.integrationProvider)?.label ?? "None"}</span>
      </ReviewRow>
      {form.integrationProvider === "mindbody" && form.siteId && (
        <ReviewRow><span>Site ID</span><span>{form.siteId}</span></ReviewRow>
      )}
      {mbActivationCode && (
        <ReviewRow>
          <span>Activation code</span>
          <span style={{ fontFamily: "monospace", letterSpacing: 1 }}>{mbActivationCode}</span>
        </ReviewRow>
      )}
    </ReviewSection>

    {error && <ErrorText style={{ marginBottom: 8 }}>{error}</ErrorText>}
    <NavRow>
      <SecondaryButton onClick={onBack} disabled={submitting}>← Back</SecondaryButton>
      <PrimaryButton onClick={onSubmit} disabled={submitting}>
        {submitting ? "Submitting…" : "Submit gym profile ✓"}
      </PrimaryButton>
    </NavRow>
  </>
);

export default GymOnboardingWizard;
